'use client';

import { LEAGUE_LABELS } from '@pickleballinc/lib/product-labels';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { DebounceInput } from 'react-debounce-input';

import { LargeScreensLeagueFilters } from '@/modules/league/search-page/LargeScreensLeagueFilters';
import { MobileLeagueFilters } from '@/modules/league/search-page/MobileLeagueFilters';
import { Country } from '@/types/players.types';

export interface FilterState {
  keyword: string;
  status: 'active' | 'completed' | string;
  country: {
    id: string;
    stateId: string;
  };
  playerGroupsId: string;
  formatsId: string;
  weekDays: {
    parameter: string;
    value: string;
  };
}

export const initialFilters: FilterState = {
  keyword: '',
  status: 'active',
  country: {
    id: '',
    stateId: '',
  },
  playerGroupsId: '',
  formatsId: '',
  weekDays: {
    parameter: '',
    value: '',
  },
};

interface FiltersProps {
  countriesData: Country[];
}

export const Filters = ({ countriesData }: FiltersProps) => {
  const searchParams = useSearchParams();
  const newSearchParams = new URLSearchParams(searchParams?.toString());

  const [filters, setFilters] = useState<FilterState>({
    keyword: searchParams?.get('keyword') || '',
    status: searchParams?.get('status') || 'active',
    country: {
      id: searchParams?.get('country') || '',
      stateId: searchParams?.get('state') || '',
    },
    playerGroupsId: searchParams?.get('player_groups') || '',
    formatsId: searchParams?.get('formats') || '',
    weekDays: {
      parameter: searchParams?.get('weekdays_parameter') || '',
      value: searchParams?.get('weekdays_value') || '',
    },
  });

  const router = useRouter();

  const updateFilter = <T extends FilterState, K extends keyof T>(key: K, value: T[K]) => {
    setFilters({ ...filters, [key]: value });
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    router.push('?');
  };

  return (
    <div className='flex w-full justify-center border-t py-2'>
      <div className='flex w-full max-w-screen-xl flex-col items-center justify-between gap-2 lg:px-4 lg:flex-row 2xl:px-0'>
        <div className='block custom-lg:hidden w-full px-4'>
          <DebounceInput
            type='text'
            className='h-12 w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-gray-200 focus:outline-none focus:ring focus:ring-gray-100'
            placeholder={`Search for ${LEAGUE_LABELS.plural}...`}
            minLength={2}
            debounceTimeout={750}
            value={filters.keyword}
            onChange={(e) => {
              newSearchParams.set('keyword', e.target.value);
              router.replace(`?${newSearchParams?.toString()}`);
              updateFilter('keyword', e.target.value);
            }}
          />
        </div>
        <div className='block custom-lg:hidden w-full'>
          <MobileLeagueFilters
            filters={filters}
            updateFilter={(key, value) => {
              updateFilter(key, value);
            }}
            resetFilters={resetFilters}
            countriesData={countriesData}
          />
        </div>
        <div className='hidden custom-lg:block w-full'>
          <LargeScreensLeagueFilters
            filters={filters}
            updateFilter={(key, value) => {
              updateFilter(key, value);
            }}
            resetFilters={resetFilters}
            countriesData={countriesData}
          />
        </div>
      </div>
    </div>
  );
};
